/**
 * The editor's asset cache: the workspace library (from the API), fonts loaded as FontFace objects (from bytes
 * fetched with the bearer, so no URL needs a token) and images as blob: URLs. The canvas re-renders when the
 * version changes.
 */
import type { Asset } from '@prism-graphics/scene-schema';
import { api, type LibraryAsset } from '../api.ts';

type Listener = () => void;

export class AssetCache {
  library: LibraryAsset[] = [];
  libraryLoaded = false;
  readonly families = new Map<string, string>();
  readonly urls = new Map<string, string>();
  readonly failed = new Set<string>();
  version = 0;
  #loading = new Map<string, Promise<void>>();
  #listeners = new Set<Listener>();

  subscribe = (fn: Listener): (() => void) => {
    this.#listeners.add(fn);
    return () => this.#listeners.delete(fn);
  };

  getVersion = (): number => this.version;

  #emit(): void {
    this.version++;
    for (const l of this.#listeners) l();
  }

  async refreshLibrary(): Promise<void> {
    const { items } = await api.listAssets();
    this.library = items;
    this.libraryLoaded = true;
    this.#emit();
  }

  byHash(hash: string): LibraryAsset | undefined {
    return this.library.find((a) => a.hash === hash);
  }

  /** Load what the scene uses (fonts before images; both in parallel per kind). */
  ensure(assets: readonly Asset[]): void {
    for (const a of assets) {
      if (this.families.has(a.hash) || this.urls.has(a.hash) || this.failed.has(a.hash) || this.#loading.has(a.hash)) continue;
      const p = (a.kind === 'font' ? this.#loadFont(a) : this.#loadMedia(a))
        .catch(() => {
          this.failed.add(a.hash);
        })
        .finally(() => {
          this.#loading.delete(a.hash);
          this.#emit();
        });
      this.#loading.set(a.hash, p);
    }
  }

  /** Resolves when everything requested so far has loaded (or failed). */
  async settled(): Promise<void> {
    while (this.#loading.size > 0) await Promise.all(this.#loading.values());
  }

  async #loadFont(a: Asset): Promise<void> {
    const bytes = await api.assetBytes(a.hash.slice(7));
    const family = `pge-${a.hash.slice(7, 19)}`;
    const face = new FontFace(family, bytes, { weight: a.fontWeight ?? 'normal', style: a.fontStyle ?? 'normal' });
    await face.load();
    document.fonts.add(face);
    this.families.set(a.hash, family);
  }

  async #loadMedia(a: Asset): Promise<void> {
    const bytes = await api.assetBytes(a.hash.slice(7));
    const url = URL.createObjectURL(new Blob([bytes], { type: a.mime }));
    if (a.kind === 'image') {
      const img = new Image();
      img.src = url;
      await img.decode().catch(() => undefined);
    }
    this.urls.set(a.hash, url);
  }

  /** A transparent pixel until the image has loaded (an empty src would be an error). */
  imageUrl = (hash: string): string => this.urls.get(hash) ?? 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
}

export const assetCache = new AssetCache();
